// What a box must BE before it may join a fleet (plan 102 slice 05, decision 7): the
// OS, the CPU architectures, the privilege and the two hardware floors, as ONE value.
//
// Before this, the five lived in four spellings: the RAM and disk floors here in
// `./specs.ts`, the Ubuntu/architecture/sudo checks only as bash in the join script
// (`apps/api/src/routes/join.ts`), their prose in `BOX_ADD_NOTE`
// (`packages/mcp/src/army/box.ts`), and the dashboard's onboarding copy in a fourth.
// Every reader now renders from {@link BOX_REQUIREMENTS}:
//
//   - `apps/api/src/routes/join.ts`      the preflight + enrollment-gate bash
//   - `packages/mcp/src/army/box.ts`     `BOX_ADD_NOTE`'s opening sentence
//   - `apps/dashboard`                   the box step's requirements line
//
// The floors are the SAME constants the server-side enroll gate refuses under
// (`checkBoxEnrollSpecFloors`), so a box the script admits is never refused later by
// a number nobody told it.

import { BOX_ENROLL_DISK_AVAILABLE_FLOOR_BYTES, BOX_ENROLL_RAM_FLOOR_BYTES } from './specs';

const MIB = 1024 * 1024;
const GIB = 1024 * MIB;

export const BOX_REQUIREMENTS = {
  /** `/etc/os-release` `ID` the join script requires: the install path is apt-based. */
  osId: 'ubuntu',
  /** How a person names {@link osId}. */
  osName: 'Ubuntu',
  /**
   * The CPU architectures the runner ships for, as people name them. The join script
   * also admits `arm64`, which is what some kernels report for `aarch64` in `uname -m`
   * ({@link BOX_UNAME_MACHINES}).
   */
  architectures: ['x86_64', 'aarch64'],
  /**
   * Root, or sudo with NO password: the join is piped into bash with no TTY, so a
   * password prompt would hang it. The script refuses anything else up front.
   */
  sudo: 'passwordless',
  /** RAM floor in MiB of MemTotal — an 8 GB box reports ~7751 MiB after reservations. */
  ramFloorMib: BOX_ENROLL_RAM_FLOOR_BYTES / MIB,
  /** The nominal box size the RAM floor admits, as it is sold. */
  ramNominalGb: 8,
  /** AVAILABLE root-filesystem space floor in GiB (the `df` Available column, not total). */
  diskAvailableFloorGib: BOX_ENROLL_DISK_AVAILABLE_FLOOR_BYTES / GIB,
} as const;

/** Every `uname -m` spelling of {@link BOX_REQUIREMENTS.architectures} the join script admits. */
export const BOX_UNAME_MACHINES: readonly string[] = [...BOX_REQUIREMENTS.architectures, 'arm64'];

/**
 * The requirements as one English clause, for operator-facing prose that is not
 * localized (`BOX_ADD_NOTE`, the join script's own messages). The dashboard renders
 * the fields through its own `t()` keys instead.
 */
export function describeBoxRequirements(): string {
  const r = BOX_REQUIREMENTS;
  return (
    `${r.osName} on ${r.architectures.join(' or ')}, root or ${r.sudo} sudo, ` +
    `at least ${r.ramNominalGb} GB RAM (${r.ramFloorMib} MiB of MemTotal) and ` +
    `${r.diskAvailableFloorGib} GiB FREE disk`
  );
}
